import { helpers as commonHelpers } from "../../../../common/helpers.ts";
import { local } from "./local.ts";
import { formatCurrencyZh, getCurrencyFormat } from "./currencyFormat.ts";

const leagueUrl = (components: (number | string | undefined)[]): string => {
	const lid = local.getState().lid;

	if (typeof lid !== "number") {
		return "/";
	}

	return commonHelpers.leagueUrlBase(lid, components);
};

const plusMinus = (arg: number, d: number): string => {
	if (Number.isNaN(arg)) {
		return "";
	}

	return (arg > 0 ? "+" : "") + arg.toFixed(d);
};

type RoundType =
	| "oneDecimalPlace"
	| "twoDecimalPlaces"
	| "threeDecimalPlaces"
	| "roundWinp"
	| "plusMinus"
	| "plusMinusNoDecimalPlace"
	| "noDecimalPlace"
	| "minutes";

// This only works for stats that are displayed the same in all contexts (per game, totals, individual game - yes, min in BBGM has a special override for individual games) - ideally would have a more comprehensive system. Currently it just assumes things that aren't the same in all contexts are 0 decimal places when totals and 1 otherwise.
const roundOverrides: Record<string, RoundType> = {
	gp: "noDecimalPlace",
	gs: "noDecimalPlace",
	min: "oneDecimalPlace",
	yearsWithTeam: "noDecimalPlace",
	gmsc: "oneDecimalPlace",
	fgp: "oneDecimalPlace",
	tpp: "oneDecimalPlace",
	"2pp": "oneDecimalPlace",
	efg: "oneDecimalPlace",
	ftp: "oneDecimalPlace",
	ws48: "roundWinp",
	pm: "plusMinus",
	pm100: "plusMinus",
	onOff100: "plusMinus",
	ftpFga: "roundWinp",
	tpar: "roundWinp",
	ftr: "roundWinp",
	bpm: "oneDecimalPlace",
	obpm: "oneDecimalPlace",
	dbpm: "oneDecimalPlace",
	vorp: "oneDecimalPlace",
	per: "oneDecimalPlace",
	ewa: "oneDecimalPlace",
	tsp: "oneDecimalPlace",
	orbp: "oneDecimalPlace",
	drbp: "oneDecimalPlace",
	trbp: "oneDecimalPlace",
	astp: "oneDecimalPlace",
	stlp: "oneDecimalPlace",
	blkp: "oneDecimalPlace",
	tovp: "oneDecimalPlace",
	usgp: "oneDecimalPlace",

	// basketball-reference has these with no decimal place for player stats, but one decimal place for team stats
	ortg: "oneDecimalPlace",
	drtg: "oneDecimalPlace",

	ows: "oneDecimalPlace",
	dws: "oneDecimalPlace",
	ws: "oneDecimalPlace",
	fgpAtRim: "oneDecimalPlace",
	fgpLowPost: "oneDecimalPlace",
	fgpMidRange: "oneDecimalPlace",
	fgMax: "noDecimalPlace",
	fgaMax: "noDecimalPlace",
	tpMax: "noDecimalPlace",
	tpaMax: "noDecimalPlace",
	"2pMax": "noDecimalPlace",
	"2paMax": "noDecimalPlace",
	ftMax: "noDecimalPlace",
	ftaMax: "noDecimalPlace",
	orbMax: "noDecimalPlace",
	drbMax: "noDecimalPlace",
	trbMax: "noDecimalPlace",
	astMax: "noDecimalPlace",
	tovMax: "noDecimalPlace",
	stlMax: "noDecimalPlace",
	blkMax: "noDecimalPlace",
	baMax: "noDecimalPlace",
	pfMax: "noDecimalPlace",
	ptsMax: "noDecimalPlace",
	pmMax: "plusMinusNoDecimalPlace",
	gmscMax: "oneDecimalPlace",
	dd: "noDecimalPlace",
	td: "noDecimalPlace",
	qd: "noDecimalPlace",
	fxf: "noDecimalPlace",
};

const toLocaleStringWithoutNegativeZero = (
	value: number,
	locales: Intl.LocalesArgument,
	options?: Intl.NumberFormatOptions,
) => {
	const output = value.toLocaleString(locales, options);
	if (output === "-0") {
		return "0";
	}
	return output;
};

const formatNumber = (value: number, type: RoundType): string => {
	if (type === "oneDecimalPlace") {
		if (value === 100) {
			return "100";
		}

		return toLocaleStringWithoutNegativeZero(value, "en-US", {
			maximumFractionDigits: 1,
			minimumFractionDigits: 1,
		});
	} else if (type === "twoDecimalPlaces") {
		return toLocaleStringWithoutNegativeZero(value, "en-US", {
			maximumFractionDigits: 2,
			minimumFractionDigits: 2,
		});
	} else if (type === "threeDecimalPlaces") {
		return toLocaleStringWithoutNegativeZero(value, "en-US", {
			maximumFractionDigits: 3,
			minimumFractionDigits: 3,
		});
	} else if (type === "roundWinp") {
		return commonHelpers.roundWinp(value);
	} else if (type === "plusMinus") {
		return plusMinus(value, 1);
	} else if (type === "plusMinusNoDecimalPlace") {
		return plusMinus(value, 0);
	} else if (type === "noDecimalPlace") {
		return toLocaleStringWithoutNegativeZero(value, "en-US", {
			maximumFractionDigits: 0,
		});
	} else if (type === "minutes") {
		if (value > 100) {
			return toLocaleStringWithoutNegativeZero(value, "en-US", {
				maximumFractionDigits: 0,
			});
		}

		if (value === 0) {
			return "--:--";
		}

		const remainder = value % 1;
		let seconds = Math.round(remainder * 60);
		let minutes = Math.floor(value);
		while (seconds >= 60) {
			minutes += 1;
			seconds -= 60;
		}
		return `${minutes}:${seconds >= 10 ? seconds : `0${seconds}`}`;
	} else {
		throw new Error("Should never happen");
	}
};

const roundStat = (value: number | string, stat: string, totals: boolean = false): string => {
	try {
		if (typeof value === "string") {
			return value;
		}

		// Number of decimals for many stats
		const decimalPlaces = totals ? 0 : 1;

		if (Number.isNaN(value)) {
			value = 0;
		}

		if (value === Infinity) {
			return "inf";
		}
		if (value === -Infinity) {
			return "-inf";
		}

		let type = roundOverrides[stat];
		if (!type && stat.startsWith("opp")) {
			const withoutOpp = stat.replace("opp", "");
			const statWithoutOpp = `${withoutOpp.charAt(0).toLowerCase()}${withoutOpp.slice(1)}`;
			type = roundOverrides[statWithoutOpp];
		}

		if (type) {
			if (type === "plusMinus" && totals) {
				return formatNumber(value, "plusMinusNoDecimalPlace");
			}
			return formatNumber(value, type);
		}

		return toLocaleStringWithoutNegativeZero(value, "en-US", {
			maximumFractionDigits: decimalPlaces,
			minimumFractionDigits: decimalPlaces,
		});
	} catch {
		return "";
	}
};

const yearRanges = (arrInput: number[]): string[] => {
	if (arrInput.length <= 1) {
		return arrInput.map(String);
	}

	const arr = [...arrInput];
	arr.sort((a, b) => a - b);

	const runArr: string[] = [];
	const tempArr = [[arr[0]!]];

	for (let i = 1; i < arr.length; i++) {
		const current = arr[i]!;
		const prev = arr[i - 1]!;

		if (current - prev > 1) {
			tempArr.push([]);
		}

		tempArr.at(-1)!.push(current);
	}

	for (const row of tempArr) {
		// runs of up to 2 consecutive years are displayed individually
		if (row.length <= 2) {
			runArr.push(String(row[0]));

			if (row.length === 2) {
				runArr.push(String(row[1]));
			}
		} else {
			// runs of 3 or more are displayed as a range
			runArr.push(`${row[0]}-${row.at(-1)}`);
		}
	}

	return runArr;
};

const formatCurrency = (amount: number, initialUnits?: "M" | "", precision?: number): string => {
	// R6-1: Chinese renders 万/亿; every other language keeps the shared base.
	const zh = formatCurrencyZh(amount, initialUnits);
	if (zh !== null) {
		return zh;
	}

	return commonHelpers.formatCurrencyBase(
		getCurrencyFormat(),
		amount,
		initialUnits,
		precision,
	);
};

export const helpers = {
	...commonHelpers,
	formatCurrency,
	formatNumber,
	leagueUrl,
	plusMinus,
	roundStat,
	yearRanges,
};
